/** Sidebar (also the mobile hamburger menu, as in the original) + page outlet. */
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import type { Permission, Role } from '@/types/models';
import { Icon, type IconName } from '@/ui/Icon';
import { ROLE_LABEL } from '@/auth/permissions';
import { ModalProvider, useAuth, useToast } from './AppContext';
import { toggleTheme } from './theme';

const NAV: { to: string; label: string; icon: IconName; perm?: Permission; permAny?: Permission[] }[] = [
  { to: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
  { to: '/projects', label: 'Projects', icon: 'projects' },
  { to: '/members', label: 'Members', icon: 'members' },
  { to: '/work-log', label: 'Work Log', icon: 'worklog' },
  { to: '/daily-report', label: 'Daily Report', icon: 'report' },
  { to: '/management', label: 'Management View', icon: 'management', perm: 'management:view' },
];

export function AppLayout() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const auth = useAuth();
  const toast = useToast();
  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [loc.pathname]);
  const show = (perm?: Permission, any?: Permission[]) => (!perm || auth.can(perm)) && (!any || any.some((p) => auth.can(p)));
  return (
    <ModalProvider>
      <div className="app">
        <aside className={'side' + (open ? ' open' : '')} id="side" aria-label="Main menu">
          <div className="brand">
            <div className="logo">✓</div>
            <div>
              Easy SaaS
              <div className="hint" style={{ fontWeight: 400 }}>
                Project tracker · template
              </div>
            </div>
          </div>
          {NAV.filter((n) => show(n.perm, n.permAny)).map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => 'nav' + (isActive ? ' active' : '')} onClick={() => setOpen(false)}>
              <Icon name={n.icon} />
              {n.label}
            </NavLink>
          ))}
          <div className="spacer" />
          {auth.user ? (
            <div className="side-user">
              <div className="hint" style={{ fontWeight: 600, color: 'var(--text)' }}>
                {auth.user.name}
              </div>
              {auth.setDemoRole ? (
                <label className="side-role">
                  <span>Demo role</span>
                  <select value={auth.user.roles[0]} onChange={(e) => void auth.setDemoRole!(e.target.value as Role)} aria-label="Demo role">
                    {(Object.keys(ROLE_LABEL) as Role[]).map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <div className="hint">{auth.user.roles.map((r) => ROLE_LABEL[r] || r).join(', ')}</div>
              )}
              <button type="button" className="btn sm" style={{ marginTop: 6 }} onClick={() => auth.logOut().catch((e: Error) => toast(e.message, true))}>
                Log Out
              </button>
            </div>
          ) : null}
          <div className="theme-toggle">
            <div
              className="switch"
              role="button"
              tabIndex={0}
              aria-label="Toggle dark mode"
              onClick={() => toggleTheme()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleTheme();
                }
              }}
            />
            Dark mode
          </div>
          {show(undefined, ['settings:write', 'backup:manage']) ? (
            <NavLink to="/settings" className={({ isActive }) => 'nav' + (isActive ? ' active' : '')} onClick={() => setOpen(false)}>
              <Icon name="settings" />
              Settings &amp; Backup
            </NavLink>
          ) : null}
        </aside>
        <main className="main" id="main">
          <Outlet />
        </main>
      </div>
      <button type="button" className="icon-btn menu-btn" style={{ position: 'fixed', top: 12, left: 12, zIndex: 45 }} aria-label="Menu" aria-expanded={open} aria-controls="side" onClick={() => setOpen((o) => !o)}>
        ☰
      </button>
    </ModalProvider>
  );
}
